import { useState } from 'react';
import { Check, Copy, Plus, ExternalLink, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button.tsx';
import type { TrackingLink } from '../../lib/types.ts';
import { EditTrackingLinkDialog } from './EditTrackingLinkDialog.tsx';
import { ImpressionsSection } from './ImpressionsSection.tsx';

interface Props {
  items: TrackingLink[] | undefined;
  loading: boolean;
  isArchived: boolean;
  onCreate: () => void;
}

export function LinksList({ items, loading, isArchived, onCreate }: Props): JSX.Element {
  if (loading) {
    return <div className="text-fg-muted">Загрузка…</div>;
  }
  if (!items || items.length === 0) {
    if (isArchived) {
      return <div className="card text-fg-muted">Теория в архиве — ссылок нет.</div>;
    }
    return (
      <div className="card flex flex-col items-center gap-3 py-8 text-center">
        <p className="max-w-md text-sm text-fg-muted">
          Tracking-ссылка — это URL, который ты вставляешь в шапку IG, описание YouTube или в любой
          другой источник трафика. По нему мы считаем визиты и клики.
        </p>
        <Button variant="primary" onClick={onCreate}>
          <Plus className="h-4 w-4" />
          Создать первую ссылку
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((link) => (
        <LinkRow key={link.id} link={link} />
      ))}
    </div>
  );
}

function LinkRow({ link }: { link: TrackingLink }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Публичный URL лендинга. Берётся из VITE_LANDING_BASE_URL (в dev
  // — http://localhost:3000 через dashboard/.env.development; в проде
  // задаётся через .env.production или окружение сборки). Fallback на
  // window.location.origin справедлив только когда дашборд и лендинг
  // под одним доменом — иначе копировался бы адрес дашборда вместо
  // лендинга.
  const landingBase =
    import.meta.env.VITE_LANDING_BASE_URL ??
    (typeof window !== 'undefined' ? window.location.origin : '');
  const publicUrl = `${landingBase}/c/${link.slug}`;

  async function copy(): Promise<void> {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="rounded-lg border border-border bg-bg-soft p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 truncate">
            <span className="font-mono text-sm">{link.slug}</span>
            <span className="rounded bg-bg-elevated px-2 py-0.5 text-xs text-fg-muted">
              {link.source}
            </span>
          </div>
          <a
            href={link.destinationUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 truncate text-xs text-fg-muted hover:text-accent"
          >
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{link.destinationUrl}</span>
          </a>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            onClick={() => setEditOpen(true)}
            className="btn-secondary text-xs"
            title="Редактировать"
          >
            <Pencil className="h-3.5 w-3.5" />
            Редактировать
          </button>
          <button onClick={() => void copy()} className="btn-secondary text-xs" title={publicUrl}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Скопировано' : 'Копировать публичный URL'}
          </button>
        </div>
      </div>
      <div className="mt-3">
        <ImpressionsSection linkId={link.id} />
      </div>
      <EditTrackingLinkDialog open={editOpen} onOpenChange={setEditOpen} link={link} />
    </div>
  );
}
